import i18next from 'i18next'
import { NormalizedBuilderNode, NodesById } from '../../../types'
import { Dimension } from '../../../../../../../../../features/dimension/types/Dimension'
import { Company } from '../../../../../../../../../types/company/Company'
import { ReportDataType } from '../../../../../../../../../redux/context/reports/types'
import { findNodeCompanyId } from './companyUtils'
import { findDimensionName } from './dimensionUtils'

// Enhanced node interface with proper typing
export interface EnhancedNode extends Omit<NormalizedBuilderNode, 'dataType'> {
  startDate?: string
  endDate?: string
  dataType?: ReportDataType
  title?: string
  dimension?: Dimension
  company?: Company
  periodGroup?: {
    startDate?: string
    endDate?: string
    dataType?: ReportDataType
    value?: string | string[] | number | number[]
    displayText?: string
  }
}

// Node with periodGroup properties
interface PeriodGroupNode extends Omit<NormalizedBuilderNode, 'dataType'> {
  dataType?: ReportDataType
}

export const enhanceNodeWithData = (
  node: NormalizedBuilderNode,
  nodesById: NodesById,
  dimensionTrees: { [companyId: string]: Dimension[] | undefined },
  companies: Company[],
  primaryCompanyId?: string
): EnhancedNode => {
  let enhancedNode: EnhancedNode = { ...node }

  if (node.type === 'dimension' && node.value) {
    // Find dimension name with company-specific logic
    const dimensionName = findDimensionName(String(node.value), node.id, nodesById, dimensionTrees)
    if (dimensionName) {
      const nodeCompanyId = findNodeCompanyId(node.id, nodesById)
      enhancedNode = {
        ...enhancedNode,
        dimension: {
          id: Number(node.value) || 0,
          companyId: nodeCompanyId || primaryCompanyId || '',
          dimensionId: String(node.value),
          name: dimensionName,
          order: 0
        }
      }
    }
  }

  if (node.type === 'company' && node.value) {
    // Find company name from companies selector
    const company = companies.find(c => String(c.id) === String(node.value))
    if (company) {
      enhancedNode = {
        ...enhancedNode,
        company
      }
    }
  }

  if (node.type === 'periodGroup') {
    const { dataType, value } = node as PeriodGroupNode
    if (value == null || dataType == null) {
      return enhancedNode
    }

    // Get translations using i18next - ensure value is a string for translation
    const normalizedValue = Array.isArray(value) ? value[0] : value
    if (normalizedValue == null) {
      return enhancedNode
    }
    const stringValue = String(normalizedValue)
    const periodTranslation = i18next.t(`customReportPage:${stringValue}`)
    const dataTypeTranslation = i18next.t(`global:${dataType}`)

    // Create display text with translations on same line
    const displayText = `${periodTranslation} (${dataTypeTranslation})`

    enhancedNode = {
      ...enhancedNode,
      dataType,
      value,
      title: displayText,
      periodGroup: {
        dataType,
        value,
        displayText
      }
    }
  }

  return enhancedNode
}
